import React from "react";
import { Line } from "react-chartjs-2";
import styles from "./RTMDashboard.module.css";
import css from "./MetricCards.module.css";
import MetricCardsSkeleton from "./MetricCardsSkeleton";
import { MetricIcon } from "../../components/MetricIcon/MetricIcon";

export interface Metric {
  title: string;
  value: string;
  percentage: number;
  chartData: {
    labels: string[];
    datasets: {
      data: number[];
      borderColor: string;
      backgroundColor?: string;
      fill: boolean;
      tension: number;
      borderWidth?: number;
    }[];
  };
}

interface MetricCardsProps {
  metrics: Metric[];
  loading?: boolean;
}

const miniChartOptions: any = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
    },
  },
  scales: {
    x: {
      display: false,
      grid: {
        display: false,
      },
    },
    y: {
      display: false,
      grid: {
        display: false,
      },
    },
  },
  elements: {
    point: {
      radius: 0,
    },
    line: {
      borderWidth: 2,
    },
  },
};

const CircularProgressBar: React.FC<{
  percent: number;
  width: number;
  strokeWidth: number;
  strokeColor: string;
  trailColor: string;
}> = ({ percent, width, strokeWidth, strokeColor, trailColor }) => {
  const radius = (width - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className={css.progressContainer}>
      <svg
        width={width}
        height={width}
        viewBox={`0 0 ${width} ${width}`}
        className={css.progressSvg}
      >
        <circle
          stroke={trailColor}
          fill="transparent"
          strokeWidth={strokeWidth}
          r={radius}
          cx={width / 2}
          cy={width / 2}
        />
        <circle
          stroke={strokeColor}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          r={radius}
          cx={width / 2}
          cy={width / 2}
          style={{
            transition: "stroke-dashoffset 0.3s ease",
          }}
        />
      </svg>
      <div className={css.progressText}>
        {Math.round(percent)}
        <span>%</span>
      </div>
    </div>
  );
};

const MetricCards: React.FC<MetricCardsProps> = ({ metrics, loading = false }) => {
  if (loading) {
    return <MetricCardsSkeleton count={4} />;
  }

  return (
    <div className={styles.metricsGrid}>
      {metrics.map((metric, index) => (
        <div key={index} className={styles.metricColContainer}>
          <div className={`${styles.metricCard} ${css.metricCardPadding}`}>
            <div className={styles.metricContent}>
              <div className={styles.metricInfo}>
                <h2 className={styles.metricValue}>{metric.value}</h2>
                <span className={styles.secondaryText}>{metric.title}</span>
              </div>
              <MetricIcon 
                iconName={
                  index === 0 ? 'total-patient' :
                  index === 1 ? 'active-monitoring' :
                  index === 2 ? 'criticak-alert' :
                  'total-clinic'
                }
                className={styles.metricIcon}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MetricCards;
